import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, api, type GetMyWorkResult } from '@whinerp/client';
import {
  ActivityQueue,
  EmptyState,
  ErrorState,
  Num,
  PageHeading,
  Skeleton,
  useToast,
  type CompletionSubmission,
  type DueSection,
} from '@whinerp/ui';
import {
  dueStateForDate,
  formatCount,
  formatRelativeDate,
  partOfDay,
  workspaceDate,
  addDaysTo,
} from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-38 My work.
 *
 * The daily driver. A rep opens this, not a record list: a list of records
 * tells you what exists, a queue tells you what to do.
 *
 * Everything here serves one interaction. Completing a step opens a popover
 * that logs the outcome and carries the next step's fields, already filled in.
 * The primary button completes and schedules; doing nothing but pressing it
 * schedules. Choosing to leave a record empty is a separate control that then
 * asks why. The default path is the one that keeps the queue alive, because
 * the default path is the one people take.
 */

type Step = GetMyWorkResult['steps'][number];

function MyWorkScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const { session } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const work = useQuery<GetMyWorkResult, ApiError>({
    queryKey: ['work'],
    queryFn: () => api.getMyWork({ query: {} }),
  });

  const complete = useMutation({
    mutationFn: (submission: CompletionSubmission) =>
      api.completeNextStep({
        path: { stepId: submission.stepId },
        body: { outcome: submission.outcome, note: submission.note, next: submission.next },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['work'] });
      toast.show(
        result.scheduledId
          ? {
              tone: 'success',
              title: t('state.saved'),
              body: `${result.recordName} · ${t('work.complete.submit')}`,
            }
          : {
              // Not a success message. Nothing is scheduled, and the toast says
              // so rather than congratulating somebody for emptying a record.
              tone: 'info',
              title: t('work.complete.noNext'),
              body: t('work.complete.noNextWarning'),
            },
      );
    },
    onError: (error: ApiError) => {
      toast.show({ tone: 'error', title: t('state.error'), body: error.message });
    },
  });

  /*
   * The reminder sweep, run when somebody opens their work.
   *
   * There is no scheduler in this product, and this is the honest stand-in: it
   * delivers reminders for EVERYONE whose work is due, not only the person
   * looking. Idempotent by date, so opening the screen six times sends one.
   */
  const reminders = useMutation({
    mutationFn: () => api.deliverReminders({ body: {} }),
    onSuccess: (result) => {
      if (result.sent > 0) {
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }
    },
  });
  const sweep = reminders.mutate;
  useEffect(() => {
    sweep();
  }, [sweep]);

  const snooze = useMutation({
    mutationFn: (input: { stepId: string; dueDate: string }) =>
      api.snoozeNextStep({ path: { stepId: input.stepId }, body: { dueDate: input.dueDate } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work'] }),
  });

  const greeting = useMemo(() => {
    // The workspace's clock, not the browser's: a rep in Dubai working a
    // workspace kept in London should be greeted by the workspace's day.
    const key = {
      morning: 'work.greeting.morning',
      afternoon: 'work.greeting.afternoon',
      evening: 'work.greeting.evening',
    } as const;
    return t(key[partOfDay(new Date(), format)], { values: { name: firstName(session.user.name) } });
  }, [t, format, session.user.name]);

  const steps = useMemo(
    () =>
      (work.data?.steps ?? []).map((step: Step) => ({
        id: step.id,
        typeId: step.typeId,
        typeLabel: step.typeLabel,
        typeIcon: step.typeIcon,
        // A calendar date compared as a calendar date. Converting it to an
        // instant puts a step due today into "overdue" for everybody west of
        // UTC, which is most of a working day for half the planet.
        section: dueStateForDate(step.dueDate, format) as DueSection,
        dueLabel: formatRelativeDate(`${step.dueDate}T12:00:00Z`, format),
        note: step.note,
        recordName: step.recordName,
        recordHref: step.recordHref,
        /*
         * Priority is shown as context rather than as a colour.
         *
         * A queue where three rows are red and forty are not is a queue people
         * read as "three things matter", and the word is what an assistive
         * reader gets - a colour alone reaches nobody who cannot see it.
         * 'normal' says nothing at all, because most things are normal and a
         * label on everything is a label on nothing.
         */
        recordContext:
          step.priority === 'high'
            ? t('work.priority.high')
            : step.priority === 'low'
              ? t('work.priority.low')
              : null,
      })),
    [work.data?.steps, format, t],
  );

  if (work.isPending) return <MyWorkLoading />;
  if (work.isError) {
    return (
      <MyWorkError error={work.error} retry={() => void work.refetch()} />
    );
  }

  const unscheduled = work.data.unscheduled.contact;

  return (
    <div data-component="MyWork" className="mx-auto flex w-full max-w-reading flex-col gap-5">
      <PageHeading
        actions={
          <Num className="text-dense text-ink-meta">
            {t('list.status.records', { values: { count: formatCount(steps.length, format) } })}
          </Num>
        }
      >
        {greeting}
      </PageHeading>

      {unscheduled > 0 ? (
        <p className="flex flex-wrap items-center gap-2 rounded-card bg-sunken px-3 py-2 text-dense text-ink-meta">
          <Num>{t('work.unscheduled', { values: { count: formatCount(unscheduled, format) } })}</Num>
          <a
            href="/contacts?nothingScheduled=true"
            className="text-bronze-text underline decoration-bronze-rule underline-offset-4"
          >
            {t('work.unscheduled.action')}
          </a>
        </p>
      ) : null}

      <ActivityQueue
        steps={steps}
        types={work.data.types}
        dueDateIn={(days) => addDaysTo(workspaceDate(new Date(), format), days)}
        labels={{
          sections: {
            overdue: t('work.section.overdue'),
            today: t('work.section.today'),
            'this-week': t('work.section.thisWeek'),
            later: t('work.section.later'),
          },
          complete: t('action.complete'),
          snooze: t('work.snooze'),
          reassign: t('work.reassign'),
          howDidItGo: t('work.complete.title'),
          outcome: t('work.complete.outcome'),
          note: t('work.complete.note'),
          andWhatIsNext: t('work.complete.nextStep'),
          nextStepType: t('work.complete.nextStepType'),
          nextStepWhen: t('work.complete.nextStepDue'),
          submitScheduling: t('work.complete.submit'),
          submitWithoutScheduling: t('work.complete.submitNothing'),
          chooseNothingNext: t('work.complete.chooseNothing'),
          keepScheduling: t('work.complete.keepScheduling'),
          nothingNextWarning: t('work.complete.nothingWarning'),
          nothingNextReason: t('work.complete.nothingReason'),
          cancel: t('action.cancel'),
          outcomes: [
            t('work.outcome.reached'),
            t('work.outcome.noAnswer'),
            t('work.outcome.rescheduled'),
            t('work.outcome.notInterested'),
            t('work.outcome.done'),
          ],
        }}
        onComplete={(submission) => complete.mutateAsync(submission).then(() => undefined)}
        onSnooze={(stepId, dueDate) => snooze.mutate({ stepId, dueDate })}
        // No reassign control until there is a way to choose a person. A
        // button wired to a handler that does nothing is a pretence, and it
        // would look exactly like the two beside it that work.
        empty={<MyWorkEmpty />}
      />
    </div>
  );
}

function firstName(name: string): string {
  return name.split(/\s+/)[0] ?? name;
}

function MyWorkEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="ListChecks"
      title={t('work.empty.title')}
      // Not a dead end: an empty queue means going and scheduling something,
      // and the state says where.
      body={t('work.empty.body')}
      primary={{ label: t('work.empty.action'), href: '/contacts', icon: 'Users' }}
    />
  );
}

function MyWorkLoading(): JSX.Element {
  const t = useT();
  return (
    <div
      className="mx-auto flex w-full max-w-reading flex-col gap-5"
      role="status"
      aria-live="polite"
      aria-busy
    >
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h2 w-block-xl" />
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable w-full" />
      ))}
    </div>
  );
}

function MyWorkError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Your work could not be loaded"
      body={error instanceof Error ? error.message : 'The queue did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-38',
  route: '/work',
  surface: 'page',
  title: 'My work',
  features: ['F-155', 'F-031'],
  entitlement: 'F-155',
  reachedBy: [{ kind: 'nav', item: 'work' }],
  // What renders with a queue in it. The empty state is a declared state and
  // is rendered and checked as one.
  components: ['ActivityQueue'],
  elements: ['MyWork'],
  states: {
    empty: MyWorkEmpty,
    loading: MyWorkLoading,
    error: MyWorkError,
  },
  component: MyWorkScreen,
});

export default screen;
